import { defineComponent, ref, computed, watch } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import { StatsHeader } from '@/components/stats-header'
import { useRegionStore } from '@/stores/region-store'
import { useSeo, useSiteBrand } from '@/composables/use-seo'
import { lotteryName, creatorOrg, partOfSite } from '@/utils/seo'
import iconLiuhe from '@/assets/stats-icons/liuhe.png'
import iconAttribute from '@/assets/stats-icons/attribute.png'
import iconSpecialHist from '@/assets/stats-icons/special-hist.png'
import iconRegularHist from '@/assets/stats-icons/regular-hist.png'
import iconTailSize from '@/assets/stats-icons/tail-size.png'
import iconZodiacSpec from '@/assets/stats-icons/zodiac-spec.png'
import iconZodiacReg from '@/assets/stats-icons/zodiac-reg.png'
import iconColorSpec from '@/assets/stats-icons/color-spec.png'
import iconColorReg from '@/assets/stats-icons/color-reg.png'
import iconSpecialSides from '@/assets/stats-icons/special-sides.png'
import iconRegularTail from '@/assets/stats-icons/regular-tail.png'
import iconRegularTotal from '@/assets/stats-icons/regular-total.png'
import iconNumberBand from '@/assets/stats-icons/number-band.png'
import iconDomesticWild from '@/assets/stats-icons/domestic-wild.png'
import iconComboTrend from '@/assets/stats-icons/combo-trend.png'
import iconZodiacCombo from '@/assets/stats-icons/zodiac-combo.png'

interface RouteConfig {
  title: string
  showPeriod: boolean
  hideHeader?: boolean // true 时 layout 不渲染 StatsHeader,由子页面自渲染
}

// 标题与 49208 chunk-zxcount 路由 meta.title 一一对应
const ROUTE_CONFIG: Record<string, RouteConfig> = {
  '/stats': { title: '六合统计', showPeriod: true },
  '/stats/attribute-ref': { title: '属性参照', showPeriod: false },
  '/stats/special-history': { title: '特码历史冷热图', showPeriod: true },
  '/stats/regular-history': { title: '正码历史冷热图', showPeriod: true },
  '/stats/tail-size': { title: '尾数大小统计图', showPeriod: false, hideHeader: true },
  '/stats/zodiac-special': { title: '生肖特码冷热图', showPeriod: true },
  '/stats/zodiac-regular': { title: '生肖正码冷热图', showPeriod: true },
  '/stats/color-special': { title: '波色特码冷热图', showPeriod: true },
  '/stats/color-regular': { title: '波色正码冷热图', showPeriod: true },
  '/stats/special-sides': { title: '特码两面', showPeriod: true },
  '/stats/special-tail': { title: '特码尾数冷热图', showPeriod: true },
  '/stats/regular-tail': { title: '正码尾数冷热图', showPeriod: true },
  '/stats/regular-total': { title: '正码总分', showPeriod: true },
  '/stats/number-band': { title: '号码波段统计图', showPeriod: true },
  '/stats/domestic-wild': { title: '家禽野兽统计图', showPeriod: false, hideHeader: true },
  '/stats/combo-trend': { title: '连码走势图', showPeriod: false, hideHeader: true },
  '/stats/zodiac-combo-trend': { title: '连肖走势图', showPeriod: false, hideHeader: true },
}

interface BottomTab {
  label: string
  icon: string
  path: string
}

const BOTTOM_TABS: BottomTab[] = [
  { label: '六合统计', icon: '统', path: '/stats' },
  { label: '属性参照', icon: '属', path: '/stats/attribute-ref' },
  { label: '特码历史', icon: '特', path: '/stats/special-history' },
  { label: '正码历史', icon: '正', path: '/stats/regular-history' },
]

interface MoreItem {
  name: string
  path: string
  img: string
}

// 与 49208 chunk-zxcount-parent-2.js 浮层 data 一一对应,本地路由替换;img 来自 49208 抽出的 png
const MORE_GROUPS: MoreItem[][] = [
  [
    { name: '六合统计', path: '/stats', img: iconLiuhe },
    { name: '属性参照', path: '/stats/attribute-ref', img: iconAttribute },
    { name: '特码历史', path: '/stats/special-history', img: iconSpecialHist },
    { name: '正码历史', path: '/stats/regular-history', img: iconRegularHist },
  ],
  [
    { name: '尾数大小', path: '/stats/tail-size', img: iconTailSize },
    { name: '生肖特码', path: '/stats/zodiac-special', img: iconZodiacSpec },
    { name: '生肖正码', path: '/stats/zodiac-regular', img: iconZodiacReg },
    { name: '波色特码', path: '/stats/color-special', img: iconColorSpec },
  ],
  [
    { name: '波色正码', path: '/stats/color-regular', img: iconColorReg },
    { name: '特码两面', path: '/stats/special-sides', img: iconSpecialSides },
    // 特码尾数没有单独的图标，与「尾数大小」共用
    { name: '特码尾数', path: '/stats/special-tail', img: iconTailSize },
    { name: '正码尾数', path: '/stats/regular-tail', img: iconRegularTail },
  ],
  [
    { name: '正码总分', path: '/stats/regular-total', img: iconRegularTotal },
    { name: '号码波段', path: '/stats/number-band', img: iconNumberBand },
    { name: '家禽野兽', path: '/stats/domestic-wild', img: iconDomesticWild },
    { name: '连码走势', path: '/stats/combo-trend', img: iconComboTrend },
  ],
  [{ name: '连肖走势', path: '/stats/zodiac-combo-trend', img: iconZodiacCombo }],
]

export default defineComponent({
  name: 'StatsTabLayout',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const regionStore = useRegionStore()
    const showConfirm = ref(false)

    const qRegion = route.query.region
    if (qRegion === 'hk' || qRegion === 'aocai') {
      regionStore.setRegion(qRegion)
    }

    watch(
      () => regionStore.region,
      () => {
        router.replace({ query: { ...route.query, region: regionStore.region } })
      },
    )

    const routeConfig = computed(
      () => ROUTE_CONFIG[route.path] ?? { title: '资讯统计', showPeriod: true },
    )

    // SEO：走势/统计页带「彩种 + 具体统计类型」动态文案与 Dataset 结构化数据。站点名取后台配置。
    const brand = useSiteBrand()
    const lotName = computed(() => lotteryName(regionStore.lotteryType))
    const statTitle = computed(() => routeConfig.value.title)
    useSeo({
      title: () => `${lotName.value}${statTitle.value}_历史开奖号码走势分析 - ${brand.value}`,
      description: () =>
        `${lotName.value}${statTitle.value}，提供平特、特码、生肖、波色等历史开奖号码走势与冷热分析，数据持续更新。`,
      keywords: () =>
        `${lotName.value},走势图,${statTitle.value},历史开奖,冷热分析,${brand.value}`,
      jsonLd: () => ({
        '@context': 'https://schema.org',
        '@type': 'Dataset',
        name: `${lotName.value}走势图数据（${statTitle.value}）`,
        description: `${lotName.value}${statTitle.value}历史开奖号码数据集，覆盖平特、特码、生肖、波色等维度的历史走势与冷热分析。`,
        creator: creatorOrg(brand.value),
        isPartOf: partOfSite(brand.value),
      }),
    })

    const isActive = (p: string) => route.path === p
    // /stats/more 没有底部 tab 直接对应项,在"更多"按钮上视为激活
    const isMoreActive = computed(
      () => !BOTTOM_TABS.some((t) => isActive(t.path)) && route.path.startsWith('/stats'),
    )

    /** 底栏/更多入口的链接地址（与 goto 的去向一致），给键盘和爬虫用；点击仍走 goto */
    const hrefOf = (p: string) => router.resolve({ path: p, query: { region: regionStore.region } }).href

    const goto = (p: string) => {
      showConfirm.value = false
      if (route.path !== p) {
        router.push({ path: p, query: { region: regionStore.region } })
      }
    }

    /** 「更多」弹层里的一个入口 */
    const renderMoreItem = (item: MoreItem) => (
      <div class="mint-tab-item" key={item.path}>
        <a
          class={isActive(item.path) ? 'router-link-active' : ''}
          href={hrefOf(item.path)}
          onClick={(e) => {
            e.preventDefault()
            goto(item.path)
          }}
        >
          <span class="mint-tab-item-icon">
            <img src={item.img} alt={item.name} />
          </span>
          <span class="mint-tab-item-label">{item.name}</span>
        </a>
      </div>
    )

    return () => (
      <div class="hello">
        {!routeConfig.value.hideHeader && (
          <StatsHeader title={routeConfig.value.title} showPeriod={routeConfig.value.showPeriod} />
        )}
        <RouterView class="mint-tab-container" />
        <footer class="mint-tabbar is-fixed">
          {BOTTOM_TABS.map((tab) => (
            <div class="mint-tab-item" key={tab.path}>
              <a
                class={isActive(tab.path) ? 'router-link-active' : ''}
                href={hrefOf(tab.path)}
                onClick={(e) => {
                  e.preventDefault()
                  goto(tab.path)
                }}
              >
                <span class="mint-tab-item-icon">
                  <span>{tab.icon}</span>
                </span>
                <span class="mint-tab-item-label">{tab.label}</span>
              </a>
            </div>
          ))}
          <div class="mint-tab-item">
            {/* 「更多」是开关弹层的按钮：补上 `.mint-tab-item a` 的块级、上下内边距与选中底色 */}
            <button
              type="button"
              class={['btn-block py-[0.12rem]', isMoreActive.value ? 'bg-[#efefef]' : '']}
              onClick={() => {
                showConfirm.value = !showConfirm.value
              }}
            >
              <span class="mint-tab-item-icon">
                <span>···</span>
              </span>
              <span class="mint-tab-item-label">更多</span>
            </button>
          </div>
          {showConfirm.value && (
            <div class="confirm" id="footer-confirm">
              {/* 点遮罩关闭：遮罩铺一个透明关闭按钮，弹层在它上面，不再靠 stopPropagation */}
              <button
                type="button"
                class="btn-reset absolute inset-0"
                aria-label="关闭"
                onClick={() => {
                  showConfirm.value = false
                }}
              />
              <div class="footer-confirm">
                {MORE_GROUPS.map((row) => (
                  <div class="mint-tabbar" key={row[0]?.path}>
                    {row.map(renderMoreItem)}
                  </div>
                ))}
                <div class="out" />
                <div class="in" />
              </div>
            </div>
          )}
        </footer>
      </div>
    )
  },
})
